/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  DataExplorerConnect,
  DATA_EXPLORER_NAMESPACE,
} from '@opengeoweb/data-explorer';
import type { DatasetTimeseriesRequest } from '@opengeoweb/data-explorer';
import { SHARED_NAMESPACE } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import {
  genericSelectors,
  syncGroupsActions,
  uiActions,
  uiTypes,
} from '@opengeoweb/store';
import {
  EdrCollectionTargetError,
  getUserAddedEDRServices,
  normalizeEdrServiceUrl,
  resolveEdrCollectionTarget,
  timeSeriesActions,
  timeSeriesSelectors,
} from '@opengeoweb/time-series';
import type { TimeSeriesModuleState } from '@opengeoweb/time-series';
import type { AppStore } from '../../store/types';
import { viewPresetActions } from '../../store/viewPresets';
import { workspaceActions } from '../../store/workspace/reducer';
import {
  getMosaicNode,
  getViewById,
  getViewIds,
} from '../../store/workspace/selectors';

interface Choice {
  title: string;
  options: { id: string; title: string; description?: string }[];
}

/** Error whose message is already translated and can be shown to the user as is. */
class TranslatedError extends Error {}

/**
 * Coordinates the Data Explorer with the workspace and Timeseries panels.
 * Data Explorer itself does not depend on workspace or Timeseries.
 */
export const DataExplorerTimeseriesConnect: React.FC<{
  productConfigKey: string;
}> = ({ productConfigKey }) => {
  const store = useStore<AppStore & TimeSeriesModuleState>();
  const { t } = useTranslation([DATA_EXPLORER_NAMESPACE, SHARED_NAMESPACE]);
  const [choice, setChoice] = React.useState<Choice>();
  const resolveChoice = React.useRef<
    ((id: string | undefined) => void) | undefined
  >(undefined);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
      resolveChoice.current?.(undefined);
    };
  }, []);

  const choose = React.useCallback(
    async (nextChoice: Choice): Promise<string | undefined> => {
      setChoice(nextChoice);
      return new Promise((resolve) => {
        resolveChoice.current = resolve;
      });
    },
    [],
  );

  const finishChoice = (id?: string): void => {
    setChoice(undefined);
    resolveChoice.current?.(id);
    resolveChoice.current = undefined;
  };

  const addTimeseriesToWorkspace = React.useCallback(
    async ({
      title,
      collectionLinks,
      targetMapId,
    }: DatasetTimeseriesRequest): Promise<void> => {
      const workspaceId = store.getState().workspace?.id;
      const collectionUrl =
        collectionLinks.length === 1
          ? collectionLinks[0].href
          : await choose({
              title: t('dataexplorer-select-data-link'),
              options: collectionLinks.map((link) => ({
                id: link.href,
                title: link.title || link.href,
              })),
            });
      if (!collectionUrl || !mounted.current) {
        return;
      }
      const target = await resolveEdrCollectionTarget(collectionUrl);
      if (!mounted.current) {
        return;
      }
      let state = store.getState();
      if (state.workspace?.id !== workspaceId || state.workspace?.isLoading) {
        throw new TranslatedError(
          t('dataexplorer-timeseries-error-workspace-changed'),
        );
      }
      const panels = getViewIds(state).filter(
        (id) => getViewById(state, id)?.componentType === 'TimeSeries',
      );
      const linkedPanels = targetMapId
        ? panels.filter((id) => {
            const linkedMaps = genericSelectors.getLinkedTargetIds(state, id);
            return linkedMaps.length > 0
              ? linkedMaps.includes(targetMapId)
              : timeSeriesSelectors.getMapId(state, id) === targetMapId;
          })
        : [];
      const candidates = linkedPanels.length > 0 ? linkedPanels : panels;
      let panelId = candidates.length === 1 ? candidates[0] : undefined;
      if (candidates.length > 1) {
        panelId = await choose({
          title: t('dataexplorer-select-timeseries-panel'),
          options: candidates.map((id, index) => ({
            id,
            title:
              getViewById(state, id)?.title ||
              t('dataexplorer-timeseries-panel', { number: index + 1 }),
            description: timeSeriesSelectors
              .getPlotState(state, id)
              ?.plots.map((plot) => plot.title)
              .join(', '),
          })),
        });
        if (!panelId) {
          return;
        }
      }
      state = store.getState();
      if (!mounted.current) {
        return;
      }
      if (
        state.workspace?.id !== workspaceId ||
        state.workspace?.isLoading ||
        (panelId && getViewById(state, panelId)?.componentType !== 'TimeSeries')
      ) {
        throw new TranslatedError(
          t('dataexplorer-timeseries-error-workspace-changed'),
        );
      }

      // Saved services may not have loaded yet when creating the first panel.
      // Hydrate them before service actions persist the current service list.
      store.dispatch(
        timeSeriesActions.loadUserAddedTimeSeriesServices(
          getUserAddedEDRServices(),
        ),
      );
      state = store.getState();
      const existingService = timeSeriesSelectors
        .getServices(state)
        ?.find(
          (service) =>
            service.type === 'EDR' &&
            normalizeEdrServiceUrl(service.url) ===
              normalizeEdrServiceUrl(target.serviceUrl),
        );
      const service = existingService ?? {
        id: uuidv4(),
        url: target.serviceUrl,
        type: 'EDR' as const,
        name:
          target.serviceInformation.title ||
          new URL(target.serviceUrl).hostname,
        description: target.serviceInformation.description || '',
      };
      const plotId = uuidv4();
      const isNewPanel = !panelId;
      panelId ??= uuidv4();
      if (!isNewPanel && !timeSeriesSelectors.getPlotState(state, panelId)) {
        throw new TranslatedError(
          t('dataexplorer-timeseries-error-panel-loading'),
        );
      }
      if (!existingService) {
        store.dispatch(
          timeSeriesActions.upsertService({
            ...service,
            panelId,
            isUpdate: false,
          }),
        );
        store.dispatch(
          snackbarActions.openSnackbar({
            type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
            message: t('dataexplorer-timeseries-service-added', {
              service: service.name,
              host: new URL(service.url).host,
            }),
          }),
        );
      }
      if (isNewPanel) {
        const plotPreset = {
          mapId: targetMapId || '',
          plots: [{ plotId, title }],
          parameters: [],
        };
        store.dispatch(
          workspaceActions.addWorkspaceView({
            id: panelId,
            componentType: 'TimeSeries',
            initialProps: { plotPreset, services: [], syncGroupsIds: [] },
          }),
        );
        store.dispatch(
          viewPresetActions.setViewPresetInline({ panelId, isInline: true }),
        );
        const mosaicNode = getMosaicNode(store.getState());
        store.dispatch(
          workspaceActions.updateWorkspaceViews({
            mosaicNode: mosaicNode
              ? { direction: 'row', first: mosaicNode, second: panelId }
              : panelId,
          }),
        );
        // Use the same preset at mount time, so registration preserves this plot.
        store.dispatch(
          timeSeriesActions.registerTimeSeriesPreset({ panelId, plotPreset }),
        );
        if (targetMapId) {
          store.dispatch(
            syncGroupsActions.updateLinkedMap({
              panelId,
              mapIds: [targetMapId],
            }),
          );
        }
      } else {
        store.dispatch(
          timeSeriesActions.addPlot({
            panelId,
            plotId,
            title,
            origin: 'dataExplorer',
          }),
        );
      }
      store.dispatch(
        viewPresetActions.setViewPresetHasChanges({
          panelId,
          hasChanges: true,
        }),
      );
      store.dispatch(workspaceActions.markWorkspaceChanged());
      store.dispatch(
        timeSeriesActions.openCollectionSelection({
          panelId,
          plotId,
          serviceId: service.id,
          collectionId: target.collection.id,
        }),
      );
      store.dispatch(uiActions.setActiveWindowId({ activeWindowId: panelId }));
      [
        uiTypes.DialogTypes.TimeSeriesManager,
        uiTypes.DialogTypes.TimeSeriesSelect,
      ].forEach((type) =>
        store.dispatch(
          uiActions.setToggleOpenDialog({
            type,
            setOpen: true,
            sourcePanelId: panelId,
          }),
        ),
      );
    },
    [choose, store, t],
  );

  const addTimeseries = React.useCallback(
    async (request: DatasetTimeseriesRequest): Promise<void> => {
      try {
        await addTimeseriesToWorkspace(request);
      } catch (error) {
        if (error instanceof TranslatedError) {
          throw error;
        }
        if (error instanceof EdrCollectionTargetError) {
          throw new TranslatedError(
            t(`dataexplorer-timeseries-error-${error.code}`),
          );
        }
        console.error('Unable to add dataset as Timeseries: ', error);
        throw new TranslatedError(t('dataexplorer-timeseries-error-generic'));
      }
    },
    [addTimeseriesToWorkspace, t],
  );

  return (
    <>
      <DataExplorerConnect
        onAddTimeseries={addTimeseries}
        productConfigKey={productConfigKey}
      />
      <Dialog onClose={() => finishChoice()} open={!!choice}>
        <DialogTitle>{choice?.title}</DialogTitle>
        <List>
          {choice?.options.map((option) => (
            <ListItemButton
              key={option.id}
              onClick={() => finishChoice(option.id)}
            >
              <ListItemText
                primary={option.title}
                secondary={option.description}
              />
            </ListItemButton>
          ))}
        </List>
        <DialogActions>
          <Button onClick={() => finishChoice()}>
            {t('shared-confirmation-cancel', { ns: SHARED_NAMESPACE })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
